import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { MpsNotificationService } from '../../mps/services/mps-notification.service';
import { ViewAsService } from '../services/view-as.service';

const EXCLUDED_PATTERNS = ['/oauth2/', '/users/all', '/users/to-impersonate', '/lokalise/', '/roles/', '/user'];

function shouldExclude(url: string): boolean {
  return EXCLUDED_PATTERNS.some((pattern) => url.includes(pattern));
}

@Injectable()
export class ViewAsInterceptor implements HttpInterceptor {
  private _viewAsService = inject(ViewAsService);
  private _injector = inject(Injector);

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // "View as" is disabled in this environment (e.g. production): never inject
    // user_id nor block writes, regardless of any stored viewed user.
    if (!environment.allowViewAs) {
      return next.handle(req);
    }

    const viewedUserId = this._viewAsService.viewedUserId();

    if (viewedUserId) {
      // Block write operations in read-only mode
      if (['POST', 'PATCH', 'PUT', 'DELETE'].includes(req.method)) {
        // Lazy inject to avoid circular dependency with TranslateService (which uses HTTP)
        const notificationService = this._injector.get(MpsNotificationService);
        const translateService = this._injector.get(TranslateService);
        notificationService.warning(translateService.instant('messages.view_as.write_blocked'));
        return EMPTY;
      }

      // Inject user_id for GET requests
      if (req.method === 'GET' && !shouldExclude(req.url)) {
        const modifiedReq = req.clone({
          params: req.params.set('user_id', viewedUserId.toString()),
        });
        return next.handle(modifiedReq);
      }
    }

    return next.handle(req);
  }
}
